import { createFileRoute, Link, useNavigate } from '@tanstack/react-router';
import * as v from 'valibot';
import { useLingui } from '@lingui/react/macro';
import { useNgoProjectsQuery, useNgoProfileQuery, useNgoPortalsQuery } from '@/lib/data-hooks';
import { trpc } from '@/lib/trpc';
import { Suspense, useEffect, useMemo, useRef, useState } from 'react';
import { AnimatePresence, motion } from 'motion/react';
import { Skeleton } from '@worldcoo/ui/components/skeleton';
import { ChevronLeftIcon, ChevronRightIcon, PlusIcon, SearchIcon } from 'lucide-react';
import { Button } from '@worldcoo/ui/components/button';
import { MainSectionHeader, MainSectionContent } from './-components/main-section';
import { ProjectItemCard } from './-components/project-item-card';
import { FundingFilter, ProjectStatusFilter } from './-components/project-filters';
import { getProjectDisplayStatus, type ProjectDisplayStatus } from './-components/project-status-badge';

const DISPLAY_STATUS_ORDER: ProjectDisplayStatus[] = ['fundraising', 'notFundraising', 'disabled', 'funded'];

type SortableProject = {
	linkedCampaigns?: { disabled: boolean; startDate?: string; endDate?: string }[];
};

function compareByDisplayStatus(a: SortableProject, b: SortableProject) {
	const indexA = DISPLAY_STATUS_ORDER.indexOf(getProjectDisplayStatus(a, 'global'));
	const indexB = DISPLAY_STATUS_ORDER.indexOf(getProjectDisplayStatus(b, 'global'));
	return indexA - indexB;
}

const ITEMS_PER_PAGE = 10;

export const Route = createFileRoute('/ngos/projects/')({
	validateSearch: v.object({
		q: v.fallback(v.optional(v.string()), undefined),
		fundingType: v.fallback(v.optional(v.picklist(['myPortal', 'worldcoo', 'both'])), undefined),
		status: v.fallback(v.optional(v.picklist(['fundraising', 'notFundraising'])), undefined),
		page: v.fallback(v.optional(v.number()), undefined),
	}),
	loaderDeps: ({ search: { q, fundingType } }) => ({ q, fundingType }),
	loader: ({ context, deps }) => {
		context.queryClient.prefetchQuery(trpc.ngo.getProfile.queryOptions());
		context.queryClient.prefetchQuery(
			trpc.ngo.getProjects.queryOptions({ query: deps.q, fundingType: deps.fundingType }),
		);
		context.queryClient.prefetchQuery(trpc.ngo.getPortals.queryOptions());
	},
	component: RouteComponent,
});

function RouteComponent() {
	return (
		<Suspense fallback={<ProjectsListSkeleton />}>
			<ProjectsList />
		</Suspense>
	);
}

function ProjectsList() {
	const { t } = useLingui();
	const navigate = useNavigate({ from: '/ngos/projects/' });
	const { q, fundingType, status, page } = Route.useSearch();
	const ngo = useNgoProfileQuery();
	const isNgoDraft = 'isDraft' in ngo && ngo.isDraft;
	const data = useNgoProjectsQuery({ q, fundingType, status: undefined });
	const portalsData = useNgoPortalsQuery();
	const ngoPortalId = 'portals' in portalsData ? portalsData.portals[0]?.id : undefined;
	const filteredProjects = useMemo(() => {
		const filtered = status
			? data.projects.filter((p) => getProjectDisplayStatus(p, 'global') === status)
			: data.projects;
		return filtered.slice().sort(compareByDisplayStatus);
	}, [data.projects, status]);
	const [searchValue, setSearchValue] = useState(q ?? '');
	const debounceRef = useRef<ReturnType<typeof setTimeout>>(undefined);

	useEffect(() => () => clearTimeout(debounceRef.current), []);

	const handleSearchChange = (value: string) => {
		setSearchValue(value);
		clearTimeout(debounceRef.current);
		debounceRef.current = setTimeout(() => {
			navigate({
				search: { q: value || undefined, fundingType, status, page: undefined },
			});
		}, 300);
	};

	const hasNoFilters = !q && !fundingType && !status;
	const isEmpty = data.projects.length === 0 && hasNoFilters;

	const currentPage = page ?? 1;
	const totalPages = Math.ceil(filteredProjects.length / ITEMS_PER_PAGE);
	const paginatedProjects = filteredProjects.slice((currentPage - 1) * ITEMS_PER_PAGE, currentPage * ITEMS_PER_PAGE);

	const statusLabels: Record<ProjectDisplayStatus, string> = {
		fundraising: t`Fundraising`,
		disabled: t`Disabled`,
		notFundraising: t`Not fundraising`,
		funded: t`Funded`,
	};

	if (isEmpty) {
		return (
			<>
				<MainSectionHeader
					title={<h1 className="text-foreground text-2xl font-medium tracking-[-0.2px]">{t`Projects`}</h1>}
				/>
				<MainSectionContent className="items-center justify-center">
					<div className="flex flex-col items-center justify-center py-20">
						<img src="/no-projects.svg" alt="" className="mb-8 h-40 w-auto" />
						<h2 className="text-foreground mb-2 text-xl font-semibold">{t`Welcome to your projects`}</h2>
						<p className="text-muted-foreground mb-8 max-w-md text-center text-sm">
							{t`You haven't added any projects yet. Create your first project so it can become eligible to start receiving donations and generating impact.`}
						</p>
						<Button size="lg" asChild>
							<Link to="/ngos/projects/new" className="gap-2">
								<PlusIcon className="size-5" />
								{t`Create First Project`}
							</Link>
						</Button>
					</div>
				</MainSectionContent>
			</>
		);
	}

	return (
		<>
			{/* Header */}
			<MainSectionHeader
				title={<h1 className="text-foreground text-2xl font-medium tracking-[-0.2px]">{t`Projects`}</h1>}
				actions={
					<Button size="xl" asChild>
						<Link to="/ngos/projects/new" className="gap-2">
							<PlusIcon className="size-5" />
							{t`New Project`}
						</Link>
					</Button>
				}
			/>

			<MainSectionContent>
				{/* Search & Filters */}
				<div className="flex flex-col gap-3 px-4 pt-4 md:flex-row md:items-center md:justify-between md:gap-4 md:px-6">
					<div className="border-border flex h-[45px] w-full max-w-[400px] items-center gap-2 rounded-xl border bg-white px-4 md:w-[291px]">
						<SearchIcon className="text-muted-foreground size-4" />
						<input
							type="text"
							placeholder={t`Search projects by name`}
							value={searchValue}
							onChange={(e) => handleSearchChange(e.target.value)}
							className="text-foreground placeholder:text-muted-foreground flex-1 bg-transparent text-sm outline-none"
						/>
					</div>

					<div className="flex flex-wrap items-center gap-2 md:gap-4">
						<span className="text-foreground text-base font-medium tracking-[-0.2px]">{t`Filters`}</span>
						<div className="flex flex-wrap gap-2">
							<FundingFilter
								value={fundingType}
								onChange={(value) => navigate({ search: { q, fundingType: value, status, page: undefined } })}
							/>
							<ProjectStatusFilter
								value={status}
								onChange={(value) => navigate({ search: { q, fundingType, status: value, page: undefined } })}
							/>
						</div>
					</div>
				</div>

				{/* Project List */}
				<div className="flex flex-col gap-4 p-4 md:p-6">
					<AnimatePresence initial={false} mode="popLayout">
						{paginatedProjects.map((project, index) => {
							const projectStatus = getProjectDisplayStatus(project, 'global');
							const prevStatus = index > 0 ? getProjectDisplayStatus(paginatedProjects[index - 1]!, 'global') : null;
							const showHeader = projectStatus !== prevStatus;
							return (
								<motion.div
									key={project.id}
									layout
									initial={{ opacity: 0, scale: 0.96 }}
									animate={{ opacity: 1, scale: 1 }}
									exit={{ opacity: 0, scale: 0.96 }}
									transition={{ duration: 0.2, ease: [0.4, 0, 0.2, 1] }}
									className="flex flex-col gap-3"
								>
									{showHeader && (
										<h2 className="text-muted-foreground mt-2 text-xs font-semibold tracking-wider uppercase first:mt-0">
											{statusLabels[projectStatus]}
										</h2>
									)}
									<ProjectItemCard project={project} isNgoDraft={isNgoDraft} ngoPortalId={ngoPortalId} view="global" />
								</motion.div>
							);
						})}
					</AnimatePresence>

					{paginatedProjects.length === 0 && (
						<div className="text-muted-foreground flex h-32 items-center justify-center">{t`No projects found`}</div>
					)}
				</div>

				{/* Pagination */}
				{totalPages > 1 && <Pagination currentPage={currentPage} totalPages={totalPages} />}
			</MainSectionContent>
		</>
	);
}

function Pagination(props: { currentPage: number; totalPages: number }) {
	const { t } = useLingui();
	const navigate = useNavigate({ from: '/ngos/projects/' });
	const { q, fundingType, status } = Route.useSearch();

	return (
		<div className="mt-auto flex flex-wrap items-center justify-between gap-3 px-4 pb-4 md:px-6 md:pb-6">
			<Button
				variant="outline-dark"
				size="xl"
				disabled={props.currentPage <= 1}
				onClick={() => navigate({ search: { q, fundingType, status, page: props.currentPage - 1 } })}
				className="disabled:opacity-0"
			>
				<ChevronLeftIcon className="size-5" />
				{t`Previous`}
			</Button>

			<div className="flex flex-wrap gap-2">
				{Array.from({ length: props.totalPages }, (_, i) => i + 1).map((pageNum) => (
					<Button
						key={pageNum}
						variant={pageNum === props.currentPage ? 'default' : 'outline'}
						size="sm"
						onClick={() => navigate({ search: { q, fundingType, status, page: pageNum } })}
						className="w-[37px]"
					>
						{pageNum}
					</Button>
				))}
			</div>

			<Button
				variant="outline-dark"
				size="xl"
				disabled={props.currentPage >= props.totalPages}
				onClick={() => navigate({ search: { q, fundingType, status, page: props.currentPage + 1 } })}
				className="disabled:opacity-0"
			>
				{t`Next`}
				<ChevronRightIcon className="size-5" />
			</Button>
		</div>
	);
}

function ProjectsListSkeleton() {
	return (
		<div className="flex flex-1 flex-col">
			<div className="border-portal-border flex min-h-24 shrink-0 items-center justify-between border-b bg-white px-6 py-4">
				<Skeleton className="h-8 w-32" />
				<Skeleton className="h-12 w-40 rounded-xl" />
			</div>
			<div className="flex items-center justify-between px-6 pt-4">
				<Skeleton className="h-[45px] w-[291px] rounded-xl" />
				<div className="flex gap-2">
					<Skeleton className="h-[45px] w-[115px] rounded-xl" />
					<Skeleton className="h-[45px] w-[115px] rounded-xl" />
				</div>
			</div>
			<div className="flex flex-col gap-4 p-6">
				{Array.from({ length: 6 }).map((_, i) => (
					// oxlint-disable-next-line react/no-array-index-key
					<Skeleton key={`skeleton-${i}`} className="h-[128px] rounded-2xl" />
				))}
			</div>
		</div>
	);
}
